import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => {
        setSession(data.token, data.user, true);
        setState({ status: "success", message: "Your email has been verified. Redirecting..." });
        setTimeout(() => navigate(data.user?.role === "admin" ? "/admin" : "/dashboard"), 1500);
      })
      .catch((err) => {
        setState({ status: "error", message: formatApiError(err.response?.data?.detail) });
      });
  }, [token, navigate, setSession]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        data-testid="verify-email-view"
        className="w-full max-w-md border border-white/10 bg-[#121212] p-10 text-center">
        <Link to="/" className="block mb-8">
          <span className="font-heading text-3xl text-primary">7 Star</span>
          <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">Limo Services</div>
        </Link>

        {state.status === "loading" && (
          <>
            <Loader2 className="text-primary mx-auto mb-4 animate-spin" size={40} />
            <h1 className="text-2xl text-foreground mb-2">Verifying your email...</h1>
            <p className="text-sm text-muted-foreground">Just a moment while we activate your account.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="text-green-400 mx-auto mb-4" size={40} />
            <h1 className="text-2xl text-foreground mb-2" data-testid="verify-success">Email verified</h1>
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="text-red-400 mx-auto mb-4" size={40} />
            <h1 className="text-2xl text-foreground mb-2" data-testid="verify-error">Verification failed</h1>
            <p className="text-sm text-muted-foreground mb-6">{state.message}</p>
            <Button asChild className="w-full bg-primary text-primary-foreground rounded-none uppercase tracking-wide h-11">
              <Link to="/register" data-testid="verify-back-signup">Back to sign up</Link>
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
